import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { getCartItemsWithDetails, getCartTotals, updateQuantity, removeFromCart, loading } = useCart();

  const cartItems = getCartItemsWithDetails();
  const { subtotal, shipping, total } = getCartTotals();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FFF8E7] flex flex-col items-center justify-center px-4" data-testid="empty-cart">
        <ShoppingBag className="w-24 h-24 text-[#6B4F2A]/30 mb-6" />
        <h1 className="text-2xl md:text-3xl font-bold text-[#2C1E0F] mb-4">Your Cart is Empty</h1>
        <p className="text-[#574021] mb-8 text-center">
          Looks like you haven't added any products yet.
        </p>
        <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="continue-shopping-btn">
          Continue Shopping
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="cart-page">
      {/* Header */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center gap-2 text-sm text-[#574021] mb-4">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <span className="text-[#2C1E0F] font-medium">Shopping Cart</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="cart-title">
            Shopping Cart
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {cartItems.map((item) => (
              <div
                key={item.product_id}
                className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-4 md:p-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="flex gap-4">
                  {/* Image */}
                  <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${item.product_id}`}
                      className="font-semibold text-[#2C1E0F] hover:text-[#1F7A63] transition-colors line-clamp-2"
                      data-testid="cart-item-name"
                    >
                      {item.product.name}
                    </Link>
                    <p className="text-sm text-[#574021] mt-1">{item.product.weight}</p>
                    <p className="font-bold text-[#1F7A63] mt-2" data-testid="cart-item-price">
                      Rs. {item.product.price.toLocaleString()}
                    </p>

                    {/* Mobile Actions */}
                    <div className="flex items-center justify-between mt-4 md:hidden">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          disabled={loading || item.quantity <= 1}
                          className="qty-btn w-8 h-8"
                          data-testid={`cart-decrease-${item.product_id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center font-medium" data-testid={`cart-quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          disabled={loading}
                          className="qty-btn w-8 h-8"
                          data-testid={`cart-increase-${item.product_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-600 p-2"
                        data-testid={`cart-remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Desktop Actions */}
                  <div className="hidden md:flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        disabled={loading || item.quantity <= 1}
                        className="qty-btn"
                        data-testid={`cart-decrease-desktop-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-medium text-[#2C1E0F]">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        disabled={loading}
                        className="qty-btn"
                        data-testid={`cart-increase-desktop-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <p className="font-bold text-[#2C1E0F] w-28 text-right" data-testid={`cart-item-total-${item.product_id}`}>
                      Rs. {(item.product.price * item.quantity).toLocaleString()}
                    </p>

                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      disabled={loading}
                      className="text-[#574021]/50 hover:text-red-500 transition-colors p-2"
                      data-testid={`cart-remove-desktop-${item.product_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold text-[#2C1E0F] mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#574021]">
                  <span>Subtotal</span>
                  <span data-testid="summary-subtotal">Rs. {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-[#574021]">
                  <span>Shipping</span>
                  <span data-testid="summary-shipping">Rs. {shipping.toLocaleString()}</span>
                </div>
                <div className="border-t border-[#6B4F2A]/10 pt-4 flex justify-between text-[#2C1E0F] font-bold text-lg">
                  <span>Total</span>
                  <span data-testid="summary-total">Rs. {total.toLocaleString()}</span>
                </div>
              </div>

              <Link
                to="/checkout"
                className="btn-primary w-full flex items-center justify-center gap-2 py-4"
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </Link>

              <Link
                to="/shop"
                className="block text-center mt-4 text-[#1F7A63] font-medium hover:underline"
                data-testid="continue-shopping-link"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
